import type { NostrEvent } from '@nostrify/nostrify';

// Kind 38473: OpenClaw Skill
export const SKILL_KIND = 38473;

// Kind 31309: MCP Server
export const MCP_KIND = 31309;

export type SkillType = 'skill' | 'mcp';

export interface Skill {
  id: string;
  pubkey: string;
  identifier: string; // d tag
  name: string;
  description: string;
  version: string;
  type: SkillType;
  repository?: string;
  homepage?: string;
  license?: string;
  price?: number; // in sats, 0 = free
  cashuMint?: string; // preferred mint for payments
  installCommand?: string;
  tags: string[];
  createdAt: number;
  event: NostrEvent;
}

export function parseSkillEvent(event: NostrEvent): Skill | null {
  if (event.kind !== SKILL_KIND && event.kind !== MCP_KIND) return null;

  const getTag = (name: string): string | undefined => {
    return event.tags.find(([t]) => t === name)?.[1];
  };

  const getTags = (name: string): string[] => {
    return event.tags.filter(([t]) => t === name).map(([, v]) => v);
  };

  const identifier = getTag('d');
  const name = getTag('name') || getTag('title');
  const description = getTag('description') || getTag('summary') || event.content;

  if (!identifier || !name) return null;

  const priceStr = getTag('price');
  const price = priceStr ? parseInt(priceStr, 10) : 0;

  return {
    id: event.id,
    pubkey: event.pubkey,
    identifier,
    name,
    description,
    version: getTag('version') || '1.0.0',
    type: event.kind === SKILL_KIND ? 'skill' : 'mcp',
    repository: getTag('repository') || getTag('r'),
    homepage: getTag('homepage') || getTag('website'),
    license: getTag('license'),
    price: isNaN(price) ? 0 : price,
    cashuMint: getTag('mint'),
    installCommand: getTag('install'),
    tags: getTags('t'),
    createdAt: event.created_at,
    event,
  };
}

export function createSkillTags(skill: Omit<Skill, 'id' | 'pubkey' | 'createdAt' | 'event'>): string[][] {
  const tags: string[][] = [
    ['d', skill.identifier],
    ['name', skill.name],
    ['description', skill.description],
    ['version', skill.version],
    ['alt', `${skill.type === 'skill' ? 'OpenClaw Skill' : 'MCP Server'}: ${skill.name}`],
  ];

  if (skill.repository) tags.push(['repository', skill.repository]);
  if (skill.homepage) tags.push(['homepage', skill.homepage]);
  if (skill.license) tags.push(['license', skill.license]);
  if (skill.price && skill.price > 0) tags.push(['price', (skill.price * 1000).toString()]); // convert sats to msats
  if (skill.installCommand) tags.push(['install', skill.installCommand]);

  skill.tags.forEach((tag) => {
    tags.push(['t', tag]);
  });

  return tags;
}

export function generateInstallCommand(skill: Skill): string {
  if (skill.installCommand) return skill.installCommand;

  if (skill.type === 'mcp') {
    return `# Add to your MCP config:
{
  "mcpServers": {
    "${skill.identifier}": {
      "command": "npx",
      "args": ["-y", "${skill.repository || skill.identifier}"]
    }
  }
}`;
  }

  return `# Install via OpenClaw CLI
openclaw install ${skill.identifier}`;
}

export function formatPrice(sats: number): string {
  if (sats === 0) return 'Free';
  if (sats < 1000) return `${sats} sats`;
  if (sats < 1000000) return `${(sats / 1000).toFixed(1)}k sats`;
  return `${(sats / 1000000).toFixed(2)}M sats`;
}
